import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { isShareCancel } from './shareCancel'
import { holdOtaSwap } from './otaSafeToSwap'
import { log } from './logger'

export type ImageSaveMethod = 'native_share' | 'download'

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Saves an image: a download on the web; on native, the share sheet, whose
 * "Save Image" / Photos target is the way into the camera roll without a
 * photo-library permission. Resolves with how it went out, or 'cancelled';
 * throws on failure.
 */
export async function saveImageFile(
  blob: Blob,
  fileName: string,
  { title }: { title: string },
): Promise<ImageSaveMethod | 'cancelled'> {
  // A share sheet backgrounds the app; a background OTA swap would drop it.
  const release = holdOtaSwap()
  try {
    if (Capacitor.isNativePlatform()) {
      // Left in Cache: Android share targets read the file after the sheet resolves.
      const { uri } = await Filesystem.writeFile({
        path: fileName,
        data: await blobToBase64(blob),
        directory: Directory.Cache,
      })
      await Share.share({ title, files: [uri] })
      return 'native_share'
    }
    download(blob, fileName)
    return 'download'
  } catch (err) {
    if (isShareCancel(err)) return 'cancelled'
    log('image-export', 'save failed', { error: String(err), native: Capacitor.isNativePlatform() })
    throw err
  } finally {
    release()
  }
}
